import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useIntl, injectIntl } from "react-intl";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FlitServiceContext } from "../../services/flitService";
import Moment from 'react-moment';
import moment from "moment-timezone";
import { makeStyles, lighten, withStyles } from "@material-ui/core/styles";
import { Card, CardHeaderToolbar, CardBody, CardHeader } from "../../../_metronic/_partials/controls/Card";
import { useSubheader } from "../../../_metronic/layout/_core/MetronicSubheader";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Toolbar, Typography,
  Tooltip, IconButton, TableSortLabel, TablePagination, LinearProgress, Tabs, Tab,
} from "@material-ui/core";
import { Form, Row, Col, DropdownButton, Dropdown, Button, ButtonGroup } from "react-bootstrap";
import cf from '../../services/codeformatter';
import { ROLES, AIR_EXPRESS_STATUS, AIR_EXPRESS_SERVICE_TYPE, CUSTOMS_CLEARANCE_TYPE, MEASUREMENT_SYSTEM_TYPE, INCOTERMS, PACKAGE_TYPE } from "../../services/flitEnum";
import { getAirExpressStatusCssClasses, isValidDate } from './AirExpressUIHelper';
import { getDeliveryObjectivePillCssClasses, getDeliveryObjectivePillStyle } from '../Consol/ConsolUIHelper';
import cargoImage from "../../assets/images/cargo.png";
import FilterListIcon from "@material-ui/icons/FilterList";
import clsx from 'clsx';
import { forkJoin } from 'rxjs';
import CommunityEditableGrid from "../CommunityEditableGrid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./AirExpressOrders.css";
import { NewAirExpressOrderDialog } from "./NewAirExpressOrderDialog";

function desc(a, b, orderBy) {
  orderBy = orderBy == 'submittedOn' ? 'id' : orderBy;
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

// keep the list tab lean : essential / user input columns only (full detail lives on the datasheet tabs)
const headRows = [
  { id: 'status', align: 'center', disablePadding: false, label: "STATUS", sortable: true },
  { id: 'deliveryObjective', align: 'center', disablePadding: false, label: "DELIVERY_OBJECTIVE", sortable: false },
  { id: 'businessEntity', align: 'center', disablePadding: false, label: "BUSINESS_ENTITY", sortable: false },
  { id: 'submittedOn', align: 'center', disablePadding: false, label: "SUBMITTED_ON", sortable: true },
  { id: 'airWaybillNo', align: 'center', disablePadding: true, label: 'AIR_WAYBILL_NO', sortable: true },
  { id: 'totalWeight', align: 'left', disablePadding: false, label: "TOTAL_WEIGHT", sortable: false },
  { id: 'chargeableWeight', align: 'left', disablePadding: false, label: "CHARGEABLE_WEIGHT", sortable: false },
  { id: 'totalPackages', align: 'left', disablePadding: false, label: "TOTAL_PACKAGES", sortable: false },
  { id: 'estimatedDepartureOn', align: 'center', disablePadding: true, label: 'ESTIMATED_DEPARTURE_ON', sortable: true },
  { id: 'estimatedArrivalOn', align: 'center', disablePadding: true, label: 'ESTIMATED_ARRIVAL_ON', sortable: true },
  { id: 'submittedBy', align: 'center', disablePadding: true, label: 'SUBMITTED_BY' },
  { id: 'note', align: 'left', disablePadding: true, label: 'NOTE' },
  { id: 'action', align: 'center', disablePadding: false, label: 'ACTION' },
];

const StyledTableCell = withStyles(theme => ({
  body: {
    fontSize: 12,
  },
}))(TableCell);

const ACTION_KEYS = {
  EDIT_ORDER: "EDIT_ORDER",
  CANCEL: "CANCEL",
};

const STATUS_FLOW = [
  AIR_EXPRESS_STATUS.DRAFT,
  AIR_EXPRESS_STATUS.SUBMITTED,
  AIR_EXPRESS_STATUS.CONFIRMED,
  AIR_EXPRESS_STATUS.BOOKED,
  AIR_EXPRESS_STATUS.PICKED_UP,
  AIR_EXPRESS_STATUS.IN_TRANSIT,
  AIR_EXPRESS_STATUS.ARRIVED,
  AIR_EXPRESS_STATUS.CUSTOMS_CLEARED,
  AIR_EXPRESS_STATUS.DELIVERED,
];

const VALID_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'KRW', 'CAD', 'AUD', 'CHF', 'HKD', 'SGD', 'SEK', 'NOK', 'DKK', 'INR', 'BRL', 'RUB', 'MXN', 'ZAR', 'TRY', 'NZD', 'THB', 'MYR', 'IDR', 'PHP', 'VND'];

function formatCurrencyAmount(value) {
  if (!value && value !== 0) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const parts = num.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function parseFormattedNumber(value) {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
}

function AirExpressTableHead(props) {
  const intl = useIntl();
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding={"checkbox"}>
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'Select all orders' }}
          />
        </StyledTableCell>
        {headRows.map((row) => (
          <StyledTableCell
            key={row.id}
            align={row.align}
            padding={row.disablePadding ? "none" : "default"}
            sortDirection={orderBy === row.id ? order : false}
          >
            {row.sortable ? (
              <TableSortLabel
                active={orderBy === row.id}
                direction={order}
                onClick={createSortHandler(row.id)}
              >
                {intl.formatMessage({ id: row.label })}
              </TableSortLabel>
            ) : (intl.formatMessage({ id: row.label }))}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

AirExpressTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
        color: theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  spacer: {
    flex: '1 1 100%',
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  title: {
    flex: '0 0 auto',
  },
}));

const AirExpressTableToolbar = props => {
  const classes = useToolbarStyles();
  const { numSelected, user, bulkStatusAction, intl } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      <div className={classes.title}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6" id="tableTitle">
            Orders
          </Typography>
        )}
      </div>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        {numSelected > 0 ? (<>
          {user.userRole?.find(r => r.role == ROLES.OPERATOR) &&
            <DropdownButton id="dropdownStatusSelected" as={ButtonGroup} title={intl.formatMessage({ id: "ACTIONS" })} size="sm" variant="success" onSelect={e => bulkStatusAction(e)}>
              {STATUS_FLOW.map(status => (
                <Dropdown.Item key={status} eventKey={status}>{cf.format('AirExpressStatus', status, intl.locale)}</Dropdown.Item>
              ))}
              <Dropdown.Item eventKey={AIR_EXPRESS_STATUS.CANCELLED}>{cf.format('AirExpressStatus', AIR_EXPRESS_STATUS.CANCELLED, intl.locale)}</Dropdown.Item>
            </DropdownButton>
          }</>
        ) : (
          <Tooltip title="Filter list">
            <IconButton aria-label="Filter list">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Toolbar>
  );
};

AirExpressTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 100
  },
  tableWrapper: {
    overflowX: 'auto',
  },
}));

// spreadsheet field descriptors shared by the advanced grid and the grouped extra-fields grids
const ADVANCED_FIELDS = [
  { name: 'deliveryObjective', label: 'DELIVERY_OBJECTIVE', type: 'text' },
  { name: 'serviceType', label: 'SERVICE_TYPE', type: 'enum', options: Object.values(AIR_EXPRESS_SERVICE_TYPE), formatter: 'AirExpressServiceType', validationId: 'VALIDATION_SERVICE_TYPE' },
  { name: 'airWaybillNo', label: 'AIR_WAYBILL_NO', type: 'text' },
  { name: 'carrier', label: 'CARRIER', type: 'text' },
  { name: 'incoterms', label: 'INCOTERMS', type: 'enum', options: Object.values(INCOTERMS), validationId: 'VALIDATION_INCOTERMS' },
  { name: 'totalWeight', label: 'ACTUAL_WEIGHT_IN_KG', type: 'number' },
  { name: 'totalVolume', label: 'ACTUAL_VOLUME_IN_CBM', type: 'number' },
  { name: 'chargeableWeight', label: 'CHARGEABLE_WEIGHT_IN_KG', type: 'number' },
  { name: 'totalPackageType', label: 'PACKAGE_TYPE', type: 'enum', options: Object.values(PACKAGE_TYPE), formatter: 'PackageType', validationId: 'VALIDATION_PACKAGE_TYPE' },
  { name: 'totalPackages', label: 'TOTAL_PACKAGES', type: 'number' },
  { name: 'totalPieces', label: 'TOTAL_PIECES', type: 'number' },
  { name: 'pickupOn', label: 'PICKUP_ON', type: 'date' },
  { name: 'documentCutOffOn', label: 'DOCUMENT_CUT_OFF_ON', type: 'date' },
  { name: 'cargoCutOffOn', label: 'CARGO_CUT_OFF_ON', type: 'date' },
  { name: 'estimatedDepartureOn', label: 'ESTIMATED_DEPARTURE_ON', type: 'date' },
  { name: 'estimatedArrivalOn', label: 'ESTIMATED_ARRIVAL_ON', type: 'date' },
  { name: 'departedOn', label: 'DEPARTED_ON', type: 'date' },
  { name: 'deliveryMethod', label: 'DELIVERY_METHOD', type: 'text' },
  { name: 'customsClearanceType', label: 'CUSTOMS_CLEARANCE_TYPE', type: 'enum', options: Object.values(CUSTOMS_CLEARANCE_TYPE), formatter: 'CustomsClearanceType', validationId: 'VALIDATION_CUSTOMS_CLEARANCE_TYPE' },
  { name: 'entryNumber', label: 'ENTRY_NUMBER', type: 'text' },
  { name: 'note', label: 'NOTE', type: 'text' },
];

const EXTRA_FIELDS_1 = [
  { name: 'quotedWeight', label: 'QUOTED_WEIGHT', type: 'number' },
  { name: 'quotedRate', label: 'QUOTED_RATE', type: 'number' },
  { name: 'quotedRateCurrency', label: 'QUOTED_RATE_CURRENCY', type: 'currency' },
  { name: 'freightCost', label: 'FREIGHT_COST', type: 'number' },
  { name: 'freightCostCurrency', label: 'FREIGHT_COST_CURRENCY', type: 'currency' },
  { name: 'freightRevenue', label: 'FREIGHT_REVENUE', type: 'number' },
  { name: 'freightRevenueCurrency', label: 'FREIGHT_REVENUE_CURRENCY', type: 'currency' },
  { name: 'fuelSurcharge', label: 'FUEL_SURCHARGE', type: 'number' },
  { name: 'fuelSurchargeCurrency', label: 'FUEL_SURCHARGE_CURRENCY', type: 'currency' },
  { name: 'truckingCost', label: 'TRUCKING_COST', type: 'number' },
  { name: 'truckingCostCurrency', label: 'TRUCKING_COST_CURRENCY', type: 'currency' },
  { name: 'truckingRevenue', label: 'TRUCKING_REVENUE', type: 'number' },
  { name: 'truckingRevenueCurrency', label: 'TRUCKING_REVENUE_CURRENCY', type: 'currency' },
  { name: 'customsCost', label: 'CUSTOMS_COST', type: 'number' },
  { name: 'customsCostCurrency', label: 'CUSTOMS_COST_CURRENCY', type: 'currency' },
  { name: 'customsRevenue', label: 'CUSTOMS_REVENUE', type: 'number' },
  { name: 'customsRevenueCurrency', label: 'CUSTOMS_REVENUE_CURRENCY', type: 'currency' },
  { name: 'warehouseCost', label: 'WAREHOUSE_COST', type: 'number' },
  { name: 'warehouseCostCurrency', label: 'WAREHOUSE_COST_CURRENCY', type: 'currency' },
  { name: 'dutyAmount', label: 'DUTY_AMOUNT', type: 'number' },
  { name: 'dutyAmountCurrency', label: 'DUTY_AMOUNT_CURRENCY', type: 'currency' },
  { name: 'totalRevenue', label: 'TOTAL_REVENUE', type: 'number' },
  { name: 'totalRevenueCurrency', label: 'TOTAL_REVENUE_CURRENCY', type: 'currency' },
  { name: 'totalCost', label: 'TOTAL_COST', type: 'number' },
  { name: 'totalCostCurrency', label: 'TOTAL_COST_CURRENCY', type: 'currency' },
  { name: 'memo', label: 'MEMO', type: 'text' },
];

const EXTRA_FIELDS_2 = [
  { name: 'bocFee', label: 'BOC_FEE', type: 'number' },
  { name: 'bocFeeCurrency', label: 'BOC_FEE_CURRENCY', type: 'currency' },
  { name: 'isBOCBilled', label: 'IS_BOC_BILLED', type: 'boolean' },
  { name: 'fdaFilingFee', label: 'FDA_FILING_FEE', type: 'number' },
  { name: 'fdaFilingFeeCurrency', label: 'FDA_FILING_FEE_CURRENCY', type: 'currency' },
  { name: 'fdaPriorNoticeFee', label: 'FDA_PRIOR_NOTICE_FEE', type: 'number' },
  { name: 'fdaPriorNoticeFeeCurrency', label: 'FDA_PRIOR_NOTICE_FEE_CURRENCY', type: 'currency' },
  { name: 'singleBond', label: 'SINGLE_BOND', type: 'number' },
  { name: 'singleBondCurrency', label: 'SINGLE_BOND_CURRENCY', type: 'currency' },
  { name: 'insurance', label: 'INSURANCE', type: 'number' },
  { name: 'insuranceCurrency', label: 'INSURANCE_CURRENCY', type: 'currency' },
  { name: 'isFTAbenefit', label: 'IS_FTA_BENEFIT', type: 'boolean' },
  { name: 'isFDAconfirmed', label: 'IS_FDA_CONFIRMED', type: 'boolean' },
  { name: 'fdAmemo', label: 'FDA_MEMO', type: 'text' },
  { name: 'ior', label: 'IOR', type: 'text' },
  { name: 'isInvoiceIssued', label: 'IS_INVOICE_ISSUED', type: 'boolean' },
  { name: 'isTaxInvoiceIssued', label: 'IS_TAX_INVOICE_ISSUED', type: 'boolean' },
];

const EXTRA_FIELDS = [...EXTRA_FIELDS_1, ...EXTRA_FIELDS_2];
const EXTRA_FIELD_NAMES = EXTRA_FIELDS.map(f => f.name);
const EXTRA_GRID_MAX_HEIGHT = 600;
const EXTRA_GRID_MIN_HEIGHT = 108;
const EXTRA_GRID_HEADER_HEIGHT = 52;
const EXTRA_GRID_ROW_HEIGHT = 26;
const EXTRA_GRID_SCROLLBAR_HEIGHT = 18;
const EXTRA_FIELD_GROUPS = [
  {
    title: 'AIR_EXPRESS_EXTRA_FREIGHT',
    fields: [
      'quotedWeight', 'quotedRate', 'quotedRateCurrency',
      'freightCost', 'freightCostCurrency', 'freightRevenue', 'freightRevenueCurrency',
      'fuelSurcharge', 'fuelSurchargeCurrency',
      'truckingCost', 'truckingCostCurrency', 'truckingRevenue', 'truckingRevenueCurrency',
    ],
  },
  {
    title: 'AIR_EXPRESS_EXTRA_CUSTOMS_TOTALS',
    fields: [
      'customsCost', 'customsCostCurrency', 'customsRevenue', 'customsRevenueCurrency',
      'warehouseCost', 'warehouseCostCurrency', 'dutyAmount', 'dutyAmountCurrency',
      'totalRevenue', 'totalRevenueCurrency', 'totalCost', 'totalCostCurrency',
      'bocFee', 'bocFeeCurrency', 'isBOCBilled',
    ],
  },
  {
    title: 'AIR_EXPRESS_EXTRA_COMPLIANCE_BILLING',
    fields: [
      'fdaFilingFee', 'fdaFilingFeeCurrency', 'fdaPriorNoticeFee', 'fdaPriorNoticeFeeCurrency',
      'singleBond', 'singleBondCurrency', 'insurance', 'insuranceCurrency',
      'isFTAbenefit', 'isFDAconfirmed', 'fdAmemo', 'ior',
      'isInvoiceIssued', 'isTaxInvoiceIssued', 'memo',
    ],
  },
];

// extras stay flattened on each shared row so all three sections edit the same underlying order
const EXTRA_KEY_PREFIX = 'extra_';
const GRID_FIELDS = [
  ...ADVANCED_FIELDS.map(f => ({ ...f, key: f.name, group: 'advanced' })),
  ...EXTRA_FIELDS.map(f => ({ ...f, key: EXTRA_KEY_PREFIX + f.name, group: 'extra' })),
];
const GRID_FIELD_BY_KEY = GRID_FIELDS.reduce((acc, f) => {
  acc[f.key] = f;
  return acc;
}, {});

// column order mirrors the "2025.09~" operations worksheet :
// No.(B) ë¶„ë¥˜(C) ì—…ì²´ëª…(D) AWB(E) ìš´ì†¡ì—…ì²´(F) í”½ì—…ì¼(G) ì„œë¥˜ë§ˆê°/ì¹´ê³ ë§ˆê°(H) ìœ ë¥˜í• ì¦ë£Œ(I) ETD(J) ETA(K)
// ì¶œê³ ì¼(L) ì•„ì´í…œ(M) ì´ íŒ¨í‚¤ì§€(N) ìˆ˜ëŸ‰(O) ì¤‘ëŸ‰(P) ë¶€í”¼(Q) ìž…ê³ ë°©ì‹(R) í†µê´€ë°©ì‹(S) BOC(T) ì§„í–‰ìƒí™©(U)
// ENTRY NUMBER(V) í™•ì¸í•„ìš”(W) íŠ¹ì´ì‚¬í•­(X) íŠ¸ëŸ­ë°°ì°¨(Y) ì²­êµ¬ì¤‘ëŸ‰(Z) ê´€ì„¸(AA) ë§¤ì¶œê°€(AB) ì›ê°€(AC) ì²­êµ¬ì„œ/ê³„ì‚°ì„œ(AE),
// fields without a matching excel column follow at the end
const GRID_COLUMN_ORDER = [
  'orderIdLabel',
  'deliveryObjective',
  'serviceType',
  'businessEntity',
  'airWaybillNo',
  'carrier',
  'pickupOn',
  'documentCutOffOn',
  'cargoCutOffOn',
  'extra_fuelSurcharge',
  'extra_fuelSurchargeCurrency',
  'estimatedDepartureOn',
  'estimatedArrivalOn',
  'departedOn',
  'description',
  'totalPackages',
  'totalPackageType',
  'totalPieces',
  'totalWeight',
  'totalVolume',
  'deliveryMethod',
  'customsClearanceType',
  'extra_bocFee',
  'extra_bocFeeCurrency',
  'extra_isBOCBilled',
  'statusLabel',
  'entryNumber',
  'extra_isFDAconfirmed',
  'extra_fdAmemo',
  'note',
  'extra_truckingCost',
  'extra_truckingCostCurrency',
  'extra_truckingRevenue',
  'extra_truckingRevenueCurrency',
  'chargeableWeight',
  'extra_dutyAmount',
  'extra_dutyAmountCurrency',
  'extra_totalRevenue',
  'extra_totalRevenueCurrency',
  'extra_totalCost',
  'extra_totalCostCurrency',
  'extra_isInvoiceIssued',
  'extra_isTaxInvoiceIssued',
  // no matching excel column from here on
  'incoterms',
  'submittedBy',
  'requestedWeight',
  'extra_quotedWeight',
  'extra_quotedRate',
  'extra_quotedRateCurrency',
  'extra_freightCost',
  'extra_freightCostCurrency',
  'extra_freightRevenue',
  'extra_freightRevenueCurrency',
  'extra_customsCost',
  'extra_customsCostCurrency',
  'extra_customsRevenue',
  'extra_customsRevenueCurrency',
  'extra_warehouseCost',
  'extra_warehouseCostCurrency',
  'extra_fdaFilingFee',
  'extra_fdaFilingFeeCurrency',
  'extra_fdaPriorNoticeFee',
  'extra_fdaPriorNoticeFeeCurrency',
  'extra_singleBond',
  'extra_singleBondCurrency',
  'extra_insurance',
  'extra_insuranceCurrency',
  'extra_isFTAbenefit',
  'extra_ior',
  'extra_memo',
];

const CURRENCY_CHOICES = ["", ...VALID_CURRENCIES];
const BOOLEAN_CHOICES = [null, true, false];
const PINNED_AIR_COLUMNS = new Set(["orderIdLabel", "statusLabel", "businessEntity", "airWaybillNo"]);
const AIR_GRID_DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  wrapHeaderText: true,
  autoHeaderHeight: false,
};

function parseBooleanValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value === true || value === false) return value;
  const normalized = String(value).trim().toUpperCase();
  if (["Y", "YES", "TRUE", "1"].includes(normalized)) return true;
  if (["N", "NO", "FALSE", "0"].includes(normalized)) return false;
  return normalized;
}

function parseDateValue(value) {
  const trimmed = (value ?? "").toString().trim();
  if (!trimmed) return null;
  return isValidDate(trimmed) ? moment(trimmed).format("YYYY-MM-DD") : trimmed;
}

function normalizeGridValue(field, value) {
  if (field.type === "number") return parseFormattedNumber((value ?? "").toString().trim());
  if (field.type === "currency" || field.type === "enum") {
    return value ? String(value).trim().toUpperCase() : null;
  }
  if (field.type === "boolean") return parseBooleanValue(value);
  if (field.type === "date") return parseDateValue(value);
  return value ?? "";
}

function formatGridValue(field, value) {
  if (field.type === "number") return formatCurrencyAmount(value);
  if (field.type === "boolean") return value === true ? "Y" : value === false ? "N" : value ?? "";
  return value ?? "";
}

function AirExpressOrders(props) {
  const subheader = useSubheader();
  const { intl, history } = props;
  const { user } = useSelector(state => state.auth);
  const { flitSvc } = useContext(FlitServiceContext);
  const [isLoading, setIsLoading] = useState(true);
  const [airExpressList, setAirExpressList] = useState([]);
  const [dense, setDense] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('submittedOn');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [statusFilter, setStatusFilter] = React.useState('');
  // there is no aggregating entity like consol schedule : orders are scoped to a single month (no serverside pagination)
  const [month, setMonth] = React.useState(moment().format('YYYY-MM'));
  const [tabValue, setTabValue] = React.useState(0);
  const [showNewAirExpressOrderDialog, setShowNewAirExpressOrderDialog] = React.useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [gridRows, setGridRows] = useState([]);
  const [isSavingGrid, setIsSavingGrid] = useState(false);
  // pristine snapshot of every grid row keyed by order id : used for dirty checking and modified-cell highlighting
  const originalRowsRef = useRef({});

  subheader.setTitle(intl.formatMessage({ id: "AIR_EXPRESS_ORDER_MANAGEMENT" }));
  const classes = useStyles();
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, (totalRows) - page * rowsPerPage);

  function refresh() {
    setIsLoading(true);
    setSelected([]);
    var airExpressFilters = [{ raw: "status ne '" + AIR_EXPRESS_STATUS.DELETED + "'" }];
    if (month) {
      const from = moment(month + '-01', 'YYYY-MM-DD').startOf('month');
      const end = from.clone().add(1, 'month');
      airExpressFilters.push({ field: 'createdOn', operator: 'ge', value: from.toDate() });
      airExpressFilters.push({ field: 'createdOn', operator: 'lt', value: end.toDate() });
    }
    flitSvc.searchAirExpress(100, 0, 'id', 'desc', flitSvc.makeFilterString(airExpressFilters)).then(({ data }) => {
      var list = data.value?.map(item => {
        const extras = item.airExpressExtraTemp;
        return {
          ...item,
          deliveryObjective: item.deliveryObjective || '',
          serviceType: item.serviceType || '',
          airWaybillNo: item.airWaybillNo || '',
          carrier: item.carrier || '',
          incoterms: item.incoterms || item.activity.quoteRequest[0]?.incoterms || '',
          totalWeight: item.totalWeight || 0,
          totalVolume: item.totalVolume || 0,
          chargeableWeight: item.chargeableWeight ?? '',
          totalPackageType: item.totalPackageType || '',
          totalPackages: item.totalPackages || 0,
          totalPieces: item.totalPieces || 0,
          pickupOn: item.pickupOn || null,
          documentCutOffOn: item.documentCutOffOn || null,
          cargoCutOffOn: item.cargoCutOffOn || null,
          estimatedDepartureOn: item.estimatedDepartureOn || null,
          estimatedArrivalOn: item.estimatedArrivalOn || null,
          departedOn: item.departedOn || null,
          deliveryMethod: item.deliveryMethod || '',
          customsClearanceType: item.customsClearanceType || '',
          entryNumber: item.entryNumber || '',
          note: item.note || '',
          airExpressExtraTemp: extras ? EXTRA_FIELD_NAMES.reduce((acc, name) => {
            acc[name] = extras[name] ?? null;
            return acc;
          }, { airExpressId: item.id }) : null
        };
      });
      setAirExpressList(list ?? []);
      setTotalRows((list ?? []).filter(row => !statusFilter ? row.status != AIR_EXPRESS_STATUS.CANCELLED : row.status == statusFilter)?.length);
    }).finally(() => {
      setIsLoading(false);
    });
  }

  useEffect(() => {
    refresh();
  }, [month]);

  useEffect(() => {
    setTotalRows(airExpressList.filter(row => !statusFilter ? row.status != AIR_EXPRESS_STATUS.CANCELLED : row.status == statusFilter)?.length);
    setPage(0);
  }, [statusFilter]);

  const isSelected = name => selected.indexOf(name) !== -1;
  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      const newSelecteds = airExpressList
        .filter(row => !statusFilter ? row.status != AIR_EXPRESS_STATUS.CANCELLED : row.status == statusFilter)
        .map(n => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  }

  function handleClick(event, name) {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  function customAction(actionKey, id) {
    switch (actionKey) {
      case ACTION_KEYS.EDIT_ORDER:
        setEditingOrderId(id);
        setShowNewAirExpressOrderDialog(true);
        break;
      case ACTION_KEYS.CANCEL:
        setIsLoading(true);
        flitSvc.updateAirExpressStatus(id, AIR_EXPRESS_STATUS.CANCELLED).then(() => {
          refresh();
        });
        break;
      default:
        // status change action
        if (Object.values(AIR_EXPRESS_STATUS).includes(actionKey)) {
          setIsLoading(true);
          flitSvc.updateAirExpressStatus(id, actionKey).then(() => {
            refresh();
          });
        }
        break;
    }
  }

  function bulkStatusAction(status) {
    if (!selected || selected.length == 0) return;
    setIsLoading(true);
    var updateObservable = [];
    selected.forEach((id) => {
      updateObservable.push(flitSvc.updateAirExpressStatus(id, status));
    });
    forkJoin(updateObservable).subscribe(res => {
      refresh();
    }, err => {
      //
    });
  }

  function fieldValueForGrid(value, field) {
    switch (field.type) {
      case 'date':
        return value ? moment(value).format('YYYY-MM-DD') : null;
      case 'boolean':
        return value === true ? true : value === false ? false : null;
      case 'number':
        return value === null || value === undefined ? '' : String(value);
      case 'enum':
      case 'currency':
        return value || null;
      default:
        return value ?? '';
    }
  }

  function toGridRow(item) {
    const quoteRequest = item.activity.quoteRequest[0];
    const row = {
      id: item.id,
      orderIdLabel: "FLIT-A-" + item.id,
      status: item.status,
      statusLabel: cf.format('AirExpressStatus', item.status, intl.locale),
      businessEntity: quoteRequest?.submittedBy.businessEntity?.legalName ?? '',
      description: item.description || quoteRequest?.description || '',
      submittedBy: ((quoteRequest?.submittedBy.firstName ?? "") + " " + (quoteRequest?.submittedBy.lastName ?? "")).trim(),
      requestedWeight: quoteRequest ? quoteRequest.totalWeight + (quoteRequest.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb') : '',
      hasExtras: !!(item.airExpressExtraTemp && item.airExpressExtraTemp.airExpressId),
    };
    ADVANCED_FIELDS.forEach(f => {
      row[f.name] = fieldValueForGrid(item[f.name], f);
    });
    const extras = item.airExpressExtraTemp || {};
    EXTRA_FIELDS.forEach(f => {
      row[EXTRA_KEY_PREFIX + f.name] = fieldValueForGrid(extras[f.name], f);
    });
    return row;
  }

  function resetGridRows(list) {
    const rows = list.map(item => toGridRow(item));
    originalRowsRef.current = rows.reduce((acc, r) => {
      acc[r.id] = { ...r };
      return acc;
    }, {});
    setGridRows(rows);
  }

  useEffect(() => {
    resetGridRows(airExpressList);
  }, [airExpressList, intl.locale]);

  function handleTabChange(event, newValue) {
    if (!isLoading) {
      // unsaved edits survive switching between the two entity tabs; rows reset whenever the list is refreshed
      setTabValue(newValue);
    }
  }

  function openNewAirExpressOrderDialog() {
    setShowNewAirExpressOrderDialog(true);
  }

  function handleCloseNewAirExpressOrderDialog(isUpdated) {
    setShowNewAirExpressOrderDialog(false);
    setEditingOrderId(null);
    if (isUpdated) {
      refresh();
    }
  }

  function monthFilterControl(keyPrefix) {
    return (
      <Form.Group controlId={keyPrefix + "Month"} className="mr-7">
        <Form.Control type="month" value={month} onChange={e => {
          setMonth(e.target.value);
          setPage(0);
        }} />
        <Form.Text className="text-muted">
          <b>{intl.formatMessage({ id: "FILTER" })}</b>{" "}
          {intl.formatMessage({ id: "MONTH" })}
        </Form.Text>
      </Form.Group>
    );
  }

  function statusFilterControl(controlId) {
    return (<Form.Group controlId={controlId}>
      <Form.Control as="select" value={statusFilter} onChange={e => {
        setStatusFilter(e.target.value);
        setPage(0);
      }}>
        <option value=''> {intl.formatMessage({ id: "ALL_ACTIVE_ORDER" })}</option>
        {STATUS_FLOW.map(status => (
          <option key={status} value={status}>{cf.format('AirExpressStatus', status, intl.locale)}</option>
        ))}
        <option value={AIR_EXPRESS_STATUS.CANCELLED}>{cf.format('AirExpressStatus', AIR_EXPRESS_STATUS.CANCELLED, intl.locale)}</option>
      </Form.Control>
      <Form.Text className="text-muted">
        <b>{intl.formatMessage({ id: "FILTER" })}</b>{" "}
        {intl.formatMessage({ id: "STATUS" })}
      </Form.Text>
    </Form.Group>);
  }

  function getSortedAndFilteredOrders(orders) {
    return stableSort(orders, getSorting(order, orderBy))
      .filter(o =>
        !statusFilter
          ? o.status !== AIR_EXPRESS_STATUS.CANCELLED
          : o.status === statusFilter
      );
  }

  function getCellError(field, value) {
    switch (field.type) {
      case 'number':
        return value !== '' && value !== null && value !== undefined && isNaN(value)
          ? intl.formatMessage({ id: "VALIDATION_NUMBER" }) : null;
      case 'currency':
        return value && !VALID_CURRENCIES.includes(value)
          ? intl.formatMessage({ id: "VALIDATION_CURRENCY" }) : null;
      case 'enum':
        return value && !field.options.includes(value)
          ? intl.formatMessage({ id: field.validationId }) : null;
      case 'boolean':
        return value === null || value === undefined || value === '' || value === true || value === false
          ? null : intl.formatMessage({ id: "VALIDATION_YES_NO" });
      case 'date':
        return value && !isValidDate(value)
          ? intl.formatMessage({ id: "VALIDATION_DATE" }) : null;
      default:
        return null;
    }
  }

  function getRowChanges(row) {
    const original = originalRowsRef.current[row.id];
    if (!original) return { advanced: false, extra: false };
    return {
      advanced: ADVANCED_FIELDS.some(f => (row[f.name] ?? '') !== (original[f.name] ?? '')),
      extra: EXTRA_FIELDS.some(f => (row[EXTRA_KEY_PREFIX + f.name] ?? '') !== (original[EXTRA_KEY_PREFIX + f.name] ?? '')),
    };
  }

  const visibleGridRows = useMemo(
    () => getSortedAndFilteredOrders(gridRows),
    [gridRows, order, orderBy, statusFilter]
  );

  const gridCellErrors = useMemo(() => {
    const map = {};
    const advancedList = [];
    const extraList = [];
    gridRows.forEach(row => {
      GRID_FIELDS.forEach(field => {
        const message = getCellError(field, row[field.key]);
        if (message) {
          map[row.id + ':' + field.key] = message;
          (field.group === 'advanced' ? advancedList : extraList).push({
            key: row.id + ':' + field.key,
            label: row.orderIdLabel + ' Â· ' + intl.formatMessage({ id: field.label }) + ' : ' + message,
          });
        }
      });
    });
    return { map, advancedList, extraList };
  }, [gridRows, intl.locale]);

  const gridChanges = useMemo(() => {
    let advanced = false;
    let extra = false;
    gridRows.forEach(row => {
      const changes = getRowChanges(row);
      advanced = advanced || changes.advanced;
      extra = extra || changes.extra;
    });
    return { advanced, extra };
  }, [gridRows]);

  const gridCellClassName = useCallback(({ data, colDef }) => {
    const columnId = colDef.field;
    if (!data || !columnId || !GRID_FIELD_BY_KEY[columnId]) return undefined;
    if (gridCellErrors.map[data.id + ':' + columnId]) return 'ag-cell-invalid';
    const original = originalRowsRef.current[data.id];
    if (original && (data[columnId] ?? '') !== (original[columnId] ?? '')) return 'ag-cell-modified';
    return undefined;
  }, [gridCellErrors]);

  const { advancedColumns, extraColumnGroups } = useMemo(() => {
    const readOnlyColumn = (key, label, width) => ({
      field: key,
      headerName: intl.formatMessage({ id: label }),
      editable: false,
      width,
      minWidth: width,
    });
    const fieldColumn = (field) => {
      const width = field.type === 'currency' ? 82
        : field.type === 'boolean' ? 78
          : field.type === 'number' ? 110
            : field.type === 'date' ? 100
              : field.type === 'enum' ? 120
                : 140;
      const column = {
        field: field.key,
        headerName: intl.formatMessage({ id: field.label }),
        editable: true,
        width,
        minWidth: width,
        valueParser: params => normalizeGridValue(field, params.newValue),
        valueFormatter: params => field.formatter && params.value
          ? cf.format(field.formatter, params.value, intl.locale)
          : formatGridValue(field, params.value),
        cellClass: gridCellClassName,
        tooltipValueGetter: params => gridCellErrors.map[params.data.id + ':' + field.key] || null,
      };
      if (field.type === 'enum') {
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = { values: ['', ...field.options] };
      } else if (field.type === 'currency') {
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = { values: CURRENCY_CHOICES };
      } else if (field.type === 'boolean') {
        column.cellEditor = 'agSelectCellEditor';
        column.cellEditorParams = { values: BOOLEAN_CHOICES };
      }
      return column;
    };
    const columnByKey = {
      orderIdLabel: {
        ...readOnlyColumn('orderIdLabel', 'ORDER_ID', 135),
        cellClass: 'community-order-id-cell',
      },
      statusLabel: readOnlyColumn('statusLabel', 'STATUS', 110),
      businessEntity: readOnlyColumn('businessEntity', 'BUSINESS_ENTITY', 150),
      description: readOnlyColumn('description', 'DESCRIPTION', 150),
      submittedBy: readOnlyColumn('submittedBy', 'SUBMITTED_BY', 120),
      requestedWeight: readOnlyColumn('requestedWeight', 'REQUESTED_WEIGHT', 110),
    };
    GRID_FIELDS.forEach(field => {
      columnByKey[field.key] = fieldColumn(field);
    });
    const orderedKeys = [
      ...GRID_COLUMN_ORDER.filter(key => columnByKey[key]),
      ...Object.keys(columnByKey).filter(key => !GRID_COLUMN_ORDER.includes(key)),
    ];
    const advancedKeys = [
      'orderIdLabel', 'statusLabel', 'businessEntity', 'airWaybillNo', 'description', 'submittedBy', 'requestedWeight',
      ...orderedKeys.filter(key => GRID_FIELD_BY_KEY[key] && !key.startsWith(EXTRA_KEY_PREFIX) && key !== 'airWaybillNo'),
    ];
    const extraIdentityColumns = [
      {
        ...readOnlyColumn('orderIdLabel', 'ORDER_ID', 135),
        cellClass: 'community-order-id-cell',
      },
      readOnlyColumn('statusLabel', 'STATUS', 90),
      readOnlyColumn('businessEntity', 'BUSINESS_ENTITY', 130),
      readOnlyColumn('airWaybillNo', 'AIR_WAYBILL_NO', 105),
    ].map(column => ({ ...column, initialPinned: 'left' }));
    const compactExtraColumn = (name) => {
      const field = EXTRA_FIELDS.find(item => item.name === name);
      const width = name === 'fuelSurcharge' ? 110
        : field.type === 'number' ? 80
          : field.type === 'currency' ? 82
          : field.type === 'boolean' ? 78
            : 90;
      return { ...columnByKey[EXTRA_KEY_PREFIX + name], width, minWidth: width };
    };
    return {
      advancedColumns: advancedKeys.map(key => ({
        ...columnByKey[key],
        pinned: PINNED_AIR_COLUMNS.has(key) ? 'left' : null,
      })),
      extraColumnGroups: EXTRA_FIELD_GROUPS.map(group => ({
        title: intl.formatMessage({ id: group.title }),
        columns: [
          ...extraIdentityColumns,
          ...group.fields.map(compactExtraColumn),
        ],
      })),
    };
  }, [intl.locale, gridCellClassName, gridCellErrors]);

  const handleGridCellValueChanged = useCallback(event => {
    const updatedRow = { ...event.data };
    setGridRows(previousRows => previousRows.map(row => row.id === updatedRow.id ? updatedRow : row));
  }, []);

  // Each tab saves its own entity : group is 'advanced' (air express) or 'extra' (air express extra)
  function saveGridChanges(group) {
    const observables = [];
    gridRows.forEach(row => {
      const changes = getRowChanges(row);
      if (group === 'advanced' && changes.advanced) {
        observables.push(flitSvc.updateAirExpressDataInMetric(row.id, {
          deliveryObjective: row.deliveryObjective ? row.deliveryObjective.toUpperCase().trim() : null,
          serviceType: row.serviceType || AIR_EXPRESS_SERVICE_TYPE.UNKNOWN,
          airWaybillNo: row.airWaybillNo,
          carrier: row.carrier,
          incoterms: row.incoterms || null,
          measurementSystem: MEASUREMENT_SYSTEM_TYPE.METRIC,
          totalWeight: row.totalWeight || 0,
          totalVolume: row.totalVolume || 0,
          chargeableWeight: row.chargeableWeight === '' ? null : row.chargeableWeight,
          totalPackageType: row.totalPackageType || null,
          totalPackages: row.totalPackages || null,
          totalPieces: row.totalPieces || null,
          pickupOn: row.pickupOn,
          documentCutOffOn: row.documentCutOffOn,
          cargoCutOffOn: row.cargoCutOffOn,
          estimatedDepartureOn: row.estimatedDepartureOn,
          estimatedArrivalOn: row.estimatedArrivalOn,
          departedOn: row.departedOn,
          deliveryMethod: row.deliveryMethod,
          customsClearanceType: row.customsClearanceType || null,
          entryNumber: row.entryNumber,
          note: row.note,
        }));
      }
      if (group === 'extra' && changes.extra) {
        const extraData = EXTRA_FIELDS.reduce((acc, f) => {
          const value = row[EXTRA_KEY_PREFIX + f.name];
          acc[f.name] = value === '' || value === undefined ? null : value;
          return acc;
        }, { airExpressId: row.id });
        observables.push(row.hasExtras
          ? flitSvc.updateAirExpressExtra(extraData)
          : flitSvc.createAirExpressExtra(extraData));
      }
    });
    if (observables.length === 0) return;
    setIsSavingGrid(true);
    forkJoin(observables).subscribe(res => {
      setIsSavingGrid(false);
      refresh();
    }, err => {
      setIsSavingGrid(false);
      console.error('Error saving air express orders:', err);
    });
  }

  return (<>
    <Row>
      <Col>
        <Card>
          <CardHeader title={intl.formatMessage({ id: "AIR_EXPRESS_ORDER_MANAGEMENT" })}>
            <CardHeaderToolbar>
              <Button style={{ float: "right" }} onClick={() => {
                openNewAirExpressOrderDialog();
              }}>{intl.formatMessage({ id: "NEW_AIR_EXPRESS_ORDER" })}</Button>
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label={intl.formatMessage({ id: "AIR_EXPRESS_ORDER_LIST" })} />
              <Tab label={intl.formatMessage({ id: "ADVANCED" })} />
              <Tab label={intl.formatMessage({ id: "EXTRA_FIELDS" })} />
            </Tabs>
            {tabValue === 0 && <>
              <Row className="mt-8">
                <Col className="d-flex align-items-center flex-wrap">
                  {monthFilterControl("list")}
                  {statusFilterControl("status")}
                </Col>
              </Row>

              {!isLoading && airExpressList.length === 0 && (
                <Row className="border-top pt-5">
                  <Col style={{ textAlign: "center" }}>
                    <img
                      src={cargoImage}
                      alt="CargoImage"
                      style={{
                        height: "100px",
                        marginTop: "20px",
                        marginBottom: "10px",
                      }}
                    />
                    <br />
                    {intl.formatMessage({ id: "NO_AIR_EXPRESS_ORDER" })}
                  </Col>
                </Row>
              )}

              <Row>
                <Col>
                  {selected.length !== 0 && (<AirExpressTableToolbar numSelected={selected.length} bulkStatusAction={bulkStatusAction} user={user} intl={intl} />)}
                  {(isLoading || airExpressList.length != 0) && <>{isLoading && <LinearProgress />}
                    <div className={classes.tableWrapper}>
                      <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                      >
                        <AirExpressTableHead
                          numSelected={selected.length}
                          order={order}
                          orderBy={orderBy}
                          onSelectAllClick={handleSelectAllClick}
                          onRequestSort={handleRequestSort}
                          rowCount={totalRows}
                        >
                        </AirExpressTableHead>
                        <TableBody>
                          {stableSort(airExpressList, getSorting(order, orderBy))
                            .filter(row => !statusFilter ? row.status != AIR_EXPRESS_STATUS.CANCELLED : row.status == statusFilter)
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                              const isItemSelected = isSelected(row.id);
                              const labelId = `enhanced-table-checkbox-${index}`;
                              const quoteRequest = row.activity.quoteRequest[0];
                              return (
                                <TableRow
                                  hover
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  tabIndex={-1}
                                  key={row.id}
                                  selected={isItemSelected}
                                >
                                  <StyledTableCell padding={"checkbox"}
                                    onClick={event => handleClick(event, row.id)}
                                  >
                                    <Checkbox
                                      checked={isItemSelected}
                                      inputProps={{ 'aria-labelledby': labelId }}
                                    />
                                  </StyledTableCell>
                                  <StyledTableCell align="center" onClick={() => history.push('/airexpress/' + row.id)}><span className={getAirExpressStatusCssClasses(row.status)}>{cf.format('AirExpressStatus', row.status, intl.locale)}</span></StyledTableCell>
                                  <StyledTableCell align="center" onClick={() => history.push('/airexpress/' + row.id)}>{row.deliveryObjective && <span className={getDeliveryObjectivePillCssClasses()} style={getDeliveryObjectivePillStyle(row.deliveryObjective)}>{row.deliveryObjective}</span>}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{(quoteRequest?.submittedBy.businessEntity?.legalName)}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}><Moment format="ll">{quoteRequest?.submittedOn}</Moment></StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.airWaybillNo}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.totalWeight == 0 ? <span className="text-muted">{quoteRequest?.totalWeight + (quoteRequest?.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb')}</span> : <b>{row.totalWeight + (row.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb')}</b>}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.chargeableWeight ? <b>{row.chargeableWeight + (row.measurementSystem == MEASUREMENT_SYSTEM_TYPE.METRIC ? ' kg' : ' lb')}</b> : ''}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>
                                    {row.totalPackages ? row.totalPackages : '-'} {cf.format('PackageType', row.totalPackageType, intl.locale)}
                                  </StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.estimatedDepartureOn ? <Moment format="ll">{row.estimatedDepartureOn}</Moment> : ""}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.estimatedArrivalOn ? <Moment format="ll">{row.estimatedArrivalOn}</Moment> : ""}</StyledTableCell>
                                  <StyledTableCell align="center" onClick={() => history.push('/airexpress/' + row.id)}>{((quoteRequest?.submittedBy.firstName ?? "") + " " + (quoteRequest?.submittedBy.lastName ?? ""))}</StyledTableCell>
                                  <StyledTableCell align="left" onClick={() => history.push('/airexpress/' + row.id)}>{row.note}</StyledTableCell>
                                  <StyledTableCell align="center">
                                    {user.userRole?.find(r => r.role == ROLES.OPERATOR) &&
                                      <DropdownButton id="dropdownStatus" as={ButtonGroup} title={intl.formatMessage({ id: "ACTIONS" })} size="sm" variant="success" onSelect={e => customAction(e, row.id)}>
                                        <Dropdown.Item eventKey={ACTION_KEYS.EDIT_ORDER}>{intl.formatMessage({ id: "EDIT" })}</Dropdown.Item>
                                        {STATUS_FLOW.filter(status => status != row.status).map(status => (
                                          <Dropdown.Item key={status} eventKey={status}>{cf.format('AirExpressStatus', status, intl.locale)}</Dropdown.Item>
                                        ))}
                                        <Dropdown.Item eventKey={ACTION_KEYS.CANCEL}>{intl.formatMessage({ id: "CANCEL" })}</Dropdown.Item>
                                      </DropdownButton>
                                    }
                                  </StyledTableCell>
                                </TableRow>
                              );
                            })}
                          {emptyRows > 0 && (
                            <TableRow style={{ height: 49 * emptyRows }}>
                              <StyledTableCell colSpan={headRows.length + 1} />
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div></>
                  }
                  {!isLoading && <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    backIconButtonProps={{
                      'aria-label': `${intl.formatMessage({ id: "PREVIOUS_PAGE" })}`,
                    }}
                    nextIconButtonProps={{
                      'aria-label': `${intl.formatMessage({ id: "NEXT_PAGE" })}`,
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />}
                </Col>
              </Row>
            </>}
            {tabValue === 1 &&
              <>
                <Row className='mt-8'>
                  <Col className="d-flex align-items-center flex-wrap">
                    {monthFilterControl("advanced")}
                    {statusFilterControl("statusAdvanced")}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col>
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{intl.formatMessage({ id: "ADVANCED_HINT" })}</div>
                      {gridCellErrors.advancedList.slice(0, 5).map(e => (
                        <div className="fv-help-block" key={e.key}>{e.label}</div>
                      ))}
                      {gridCellErrors.advancedList.length > 5 &&
                        <div className="fv-help-block">+{gridCellErrors.advancedList.length - 5}</div>}
                    </div>
                  </Col>
                  <Col>
                    <button
                      type='button'
                      onClick={() => saveGridChanges('advanced')}
                      disabled={isSavingGrid || isLoading || !gridChanges.advanced || gridCellErrors.advancedList.length > 0}
                      className={`btn btn-warning btn-elevate float-right kt-login__btn-primary`}
                    >
                      {intl.formatMessage({ id: "SAVE_CHANGES" })}
                      {isSavingGrid && <span className="ml-3 mr-5 spinner spinner-white"></span>}
                    </button>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {isLoading && <LinearProgress />}
                    <div
                      className="ag-theme-alpine flit-ag-grid air-ag-grid"
                      style={{
                        height: Math.min(
                          EXTRA_GRID_MAX_HEIGHT,
                          Math.max(
                            EXTRA_GRID_MIN_HEIGHT,
                            EXTRA_GRID_HEADER_HEIGHT + EXTRA_GRID_SCROLLBAR_HEIGHT + visibleGridRows.length * EXTRA_GRID_ROW_HEIGHT
                          )
                        ),
                      }}
                    >
                      <CommunityEditableGrid
                        rowData={visibleGridRows}
                        columnDefs={advancedColumns}
                        defaultColDef={AIR_GRID_DEFAULT_COL_DEF}
                        getRowId={params => String(params.data.id)}
                        onCellValueChanged={handleGridCellValueChanged}
                        rowHeight={26}
                        headerHeight={52}
                        stopEditingWhenCellsLoseFocus
                        suppressRowClickSelection
                        suppressDragLeaveHidesColumns
                      />
                    </div>
                    <br />
                  </Col>
                </Row>
              </>
            }
            {tabValue === 2 &&
              <>
                <Row className='mt-8'>
                  <Col className="d-flex align-items-center flex-wrap">
                    {monthFilterControl("extras")}
                    {statusFilterControl("statusExtras")}
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col>
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{intl.formatMessage({ id: "EXTRA_FIELDS_HINT" })}</div>
                      {gridCellErrors.extraList.slice(0, 5).map(e => (
                        <div className="fv-help-block" key={e.key}>{e.label}</div>
                      ))}
                      {gridCellErrors.extraList.length > 5 &&
                        <div className="fv-help-block">+{gridCellErrors.extraList.length - 5}</div>}
                    </div>
                  </Col>
                  <Col>
                    <button
                      type='button'
                      onClick={() => saveGridChanges('extra')}
                      disabled={isSavingGrid || isLoading || !gridChanges.extra || gridCellErrors.extraList.length > 0}
                      className={`btn btn-warning btn-elevate float-right kt-login__btn-primary`}
                    >
                      {intl.formatMessage({ id: "SAVE_CHANGES" })}
                      {isSavingGrid && <span className="ml-3 mr-5 spinner spinner-white"></span>}
                    </button>
                  </Col>
                </Row>
                {isLoading && <LinearProgress />}
                {extraColumnGroups.map((group, index) => (
                  <Row className={index === 0 ? "mt-4" : "mt-8"} key={group.title}>
                    <Col>
                      <h6 className="mb-3 font-weight-bold air-extra-section-title">{group.title}</h6>
                      <div
                        className="ag-theme-alpine flit-ag-grid air-ag-grid"
                        style={{
                          height: Math.min(
                            EXTRA_GRID_MAX_HEIGHT,
                            Math.max(
                              EXTRA_GRID_MIN_HEIGHT,
                              EXTRA_GRID_HEADER_HEIGHT + EXTRA_GRID_SCROLLBAR_HEIGHT + visibleGridRows.length * EXTRA_GRID_ROW_HEIGHT
                            )
                          ),
                        }}
                      >
                        <CommunityEditableGrid
                          rowData={visibleGridRows}
                          columnDefs={group.columns}
                          defaultColDef={AIR_GRID_DEFAULT_COL_DEF}
                          getRowId={params => String(params.data.id)}
                          onCellValueChanged={handleGridCellValueChanged}
                          rowHeight={EXTRA_GRID_ROW_HEIGHT}
                          headerHeight={EXTRA_GRID_HEADER_HEIGHT}
                          stopEditingWhenCellsLoseFocus
                          suppressRowClickSelection
                          suppressDragLeaveHidesColumns
                        />
                      </div>
                    </Col>
                  </Row>
                ))}
              </>
            }
          </CardBody>
        </Card>
      </Col>
    </Row>

    <NewAirExpressOrderDialog show={showNewAirExpressOrderDialog} onHide={handleCloseNewAirExpressOrderDialog} orderId={editingOrderId} />
  </>
  )
}

export default injectIntl(AirExpressOrders);
